import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import {
  SportsSearchPanel,
  type SportsSearchNavigateTarget,
} from '@/ui/sports/search';

export interface SportsSearchOverlayProps {
  open: boolean;
  onClose: () => void;
}

export function SportsSearchOverlay({ open, onClose }: SportsSearchOverlayProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;

  const handleNavigate = (target: SportsSearchNavigateTarget) => {
    if (target.kind === 'category') {
      void navigate({
        to: '/sports/$sport/$category',
        params: { sport: target.sport, category: target.category },
      });
    } else if (target.kind === 'tournament' && target.tournament) {
      void navigate({
        to: '/sports/$sport/$category/$tournament',
        params: {
          sport: target.sport,
          category: target.category,
          tournament: target.tournament,
        },
      });
    } else if (target.kind === 'event' && target.tournament && target.eventId) {
      void navigate({
        to: '/sports/$sport/$category/$tournament/$eventId',
        params: {
          sport: target.sport,
          category: target.category,
          tournament: target.tournament,
          eventId: target.eventId,
        },
        search: {},
      });
    }
    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-50" role="presentation">
      <button
        type="button"
        aria-label={t('Close search', { defaultValue: 'Close search' })}
        className="absolute inset-0 bg-bg-overlay"
        onClick={onClose}
      />
      <div
        className={cn(
          // Mobile: inset panel below header
          'pointer-events-none absolute inset-x-2 top-[6.5rem]',
          // Desktop: anchored over SideNav under TopNav
          'lg:inset-x-auto lg:top-[4.5rem] lg:left-3 lg:w-[216px]',
        )}
      >
        <SportsSearchPanel
          query={query}
          onQueryChange={setQuery}
          onNavigate={handleNavigate}
          className="pointer-events-auto w-full lg:w-[216px]"
        />
      </div>
    </div>,
    document.body,
  );
}
